/**
 * Desktop updates (Tauri updater, ADR 0012). The update endpoint is the
 * `updater` function on the server this copy is connected to (Nexus Open has
 * no fixed server): it reads releases from the private GitHub repo, so
 * checking and downloading need the login. Signatures are verified by the app
 * with the built-in public key. The Rust side (src-tauri/src/updates.rs) does
 * the check and install, because the plugin's JS API can't take an address.
 */
import { useEffect } from 'react';
import { create } from 'zustand';
import { isTauri } from '@nexus/store/tauri';
import { server, serverConfigured } from '../sync/config';
import { useSyncState } from '../sync/useSync';

const CHECK_EVERY_MS = 6 * 3600_000;

/** What the last check found (src-tauri/src/updates.rs `Found`). */
export interface FoundUpdate {
  version: string;
  notes: string | null;
}

type Progress =
  | { event: 'Started'; data: { contentLength: number | null } }
  | { event: 'Progress'; data: { chunkLength: number } }
  | { event: 'Finished' };

interface UpdateUi {
  update: FoundUpdate | null;
  state: 'idle' | 'checking' | 'none' | 'downloading' | 'error';
  progress: number | null; // 0..1 while downloading
  error: string | null;
  dismissed: boolean;
}

export const useUpdates = create<UpdateUi>(() => ({
  update: null,
  state: 'idle',
  progress: null,
  error: null,
  dismissed: false,
}));

/** "flatpak" when installed as a Flatpak (updates come from Flatpak then), else null. */
let sandbox: Promise<string | null> | null = null;
export function appSandbox(): Promise<string | null> {
  sandbox ??= isTauri()
    ? import('@tauri-apps/api/core')
        .then(({ invoke }) => invoke<string | null>('app_sandbox'))
        .catch(() => null)
    : Promise.resolve(null);
  return sandbox;
}

/** The connected server's update endpoint; the updater fills in the {{…}} parts. */
const endpoint = () =>
  `${server().url}/functions/v1/updater?target={{target}}&arch={{arch}}&current={{current_version}}&bundle={{bundle_type}}`;

export async function checkForUpdate(): Promise<void> {
  const token = useSyncState.getState().session?.access_token;
  if (!isTauri() || !serverConfigured() || !token || (await appSandbox()) === 'flatpak') return;
  useUpdates.setState({ state: 'checking', error: null });
  try {
    const { invoke } = await import('@tauri-apps/api/core');
    const update = await invoke<FoundUpdate | null>('update_check', {
      endpoint: endpoint(),
      headers: { Authorization: `Bearer ${token}`, apikey: server().key },
    });
    useUpdates.setState({ update, state: update ? 'idle' : 'none', dismissed: false });
  } catch (e) {
    useUpdates.setState({ state: 'error', error: e instanceof Error ? e.message : String(e) });
  }
}

export async function installUpdate(): Promise<void> {
  const { update } = useUpdates.getState();
  if (!update) return;
  useUpdates.setState({ state: 'downloading', progress: 0, error: null });
  try {
    const { Channel, invoke } = await import('@tauri-apps/api/core');
    let total = 0;
    let done = 0;
    const onProgress = new Channel<Progress>();
    onProgress.onmessage = (e) => {
      if (e.event === 'Started') total = e.data.contentLength ?? 0;
      if (e.event === 'Progress') {
        done += e.data.chunkLength;
        useUpdates.setState({ progress: total ? done / total : null });
      }
    };
    await invoke('update_install', { onProgress });
    // Windows quits for the installer; elsewhere start the new version.
    const { relaunch } = await import('@tauri-apps/plugin-process');
    await relaunch();
  } catch (e) {
    useUpdates.setState({ state: 'error', error: e instanceof Error ? e.message : String(e) });
  }
}

/** Check at start (once signed in) and every few hours. */
export function useDesktopUpdates() {
  const signedIn = useSyncState((s) => !!s.session);
  useEffect(() => {
    if (!isTauri() || !signedIn) return;
    void checkForUpdate();
    const timer = setInterval(() => void checkForUpdate(), CHECK_EVERY_MS);
    return () => clearInterval(timer);
  }, [signedIn]);
}
